import axios from "axios";
import EncryptUtil from "../../common/utils/EncryptUtil";
import LMPAuthUtils from "./LMPAuthUtils";
import LocalStorageKey from "../../common/enums/LocalStorageKey";
import GetRoleApprovalListDummyData from "../../dummy/GetRoleApprovalListDummyData.json";

/*
    한국: KR - ko
    베트남: VN - vi
    인도네시아: ID - id
    오스트레일리아: AU - en
    캄보디아: KH - en
    스리랑카: LK - en
    말레이시아: MY - ms
    네팔: NP - ne
    뉴질랜드: NZ - en
    필리핀: PH - en
    싱가폴: SG - sg
    홍콩: HK - zh
    타이완: TW - en
    중국: CN - zh
    인도: IN - en
    태국: TH - th
    일본: JP - ja
 */

class LMPAuthApi {
    // 역할 승인 목록/유저 상세를 더미 데이터로 대체할지 여부 (LMP 역할 승인 API 미연동 구간)
    fakeApi = false;
    // Init 페이지에서 선택한 국가(없을 경우 태국)
    selectedCountry = localStorage.getItem(LocalStorageKey.server) || "TH";
    // 리액트(지금 이 프로젝트) 서버 주소
    frontServerBaseUrl = "http://localhost:8080";
    // LMP 서버 주소(선택한 국가 별 서브도메인 다름)
    baseUrl = `https://qt2-${this.selectedCountry.toLowerCase()}.emp.biz.lgeapi.com/empb2b/v1.0`;
    // 기본적으로 사용하는 헤더
    baseHeaders = {
        "X-Lge-AppKey": "a4209c397c0f4999945ed7f4b6e86578",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
        "X-Device-Publish-Flag": "N",
        "X-Device-Country": this.selectedCountry,
    };
    joinVerifiedUrl = `${this.frontServerBaseUrl}/auth/verified`;
    unlockLoginUrl = `${this.frontServerBaseUrl}/auth/login-unlock`;
    /*
     * 회원가입 API 요청
     * 필요 PathVariable: userid(이메일 주소)
     * 필요 param: password, birthday(YYYYMMDD), isSubscribe("Y"/"N"),
     *          role_id(일단 일반회원 권한으로 쓰고 있는데 필요시 추가가 가능한 듯?),
     *          target_url(회원가입 이메일 인증 버튼 클릭시 리다이렉션 될 페이지,
     *                  해당 페이지에서 아래에 있는 verifyJoinEmail 함수를 호출하여 인증 처리를 완료해야 함)
     * terms은 해당 국가 약관 조회 후 추가할 것.
     * 비밀번호는 RSA 방식으로 암호화하여 전송
     * RSA 방식으로 잠글 때 공개키는 EncryptUtil에 있으니 참고 바람.
     * 추가 필수 헤더: "X-Device-Language"(언어 코드 IETF 사용), "X-Device-Language-Type"(국가 코드 사용 타입, 그냥 IETF 쓰세요)
     */
    async join({ userid, password, birthday, isSubscribe }) {
        const body = new URLSearchParams();
        body.append("password", EncryptUtil.encryptRSAPassword(password));
        body.append("terms", LMPAuthUtils.getTermData({ countryCode: this.selectedCountry }));
        body.append("birthday", birthday);
        body.append("is_subscribe", isSubscribe);
        body.append("role_id", "USR037001");
        body.append("target_url", this.joinVerifiedUrl);

        const response = await axios.post(`${this.baseUrl}/account/user/email/${userid}`, body, {
            headers: {
                ...this.baseHeaders,
                "X-Device-Language": LMPAuthUtils.getTermLanguage({ countryCode: this.selectedCountry }),
                "X-Device-Language-Type": "IETF",
            },
        });

        return response.data;
    }

    async resendJoinMail({ userId }) {
        const response = await axios.get(
            `${this.baseUrl}/email/auth/email/${userId}?target_url=${this.joinVerifiedUrl}`,
            {
                headers: {
                    ...this.baseHeaders,
                },
            }
        );

        return response.data;
    }

    /*
     * 로그인 요청 API
     * 필요 PathVariable: userid(이메일 주소)
     * 필요 QueryString: target_url(로그인 5회 이상 틀려서 잠금 시 잠금 해제 url)
     * 필요 param: password
     * 비밀번호는 SHA-512 방식으로 암호화하여 전송(salt 값은 없음)
     */
    async login({ userid, password }) {
        const body = new URLSearchParams();
        body.append("user_auth", EncryptUtil.encryptSHA512Password(password));

        const response = await axios.post(
            `${this.baseUrl}/account/session/${userid}?target_url=${this.unlockLoginUrl}`,
            body,
            {
                headers: {
                    ...this.baseHeaders,
                },
            }
        );
        return response.data;
    }

    //
    /*
     * /account/session/lock/{사용자ID}?otp_key=otp_key
     * 로그인 잠금 해제 API
     * 필요 PathVariable: userId(이메일 주소)
     * 필요 QueryString: otp_key(메일에 쿼리스트링으로 넘어오는 authKeyNum의 값을 넣어주면 됨)
     */
    async unlockLogin({ userId, authKeyNum }) {
        const response = await axios.delete(`${this.baseUrl}/account/session/lock/${userId}?otp_key=${authKeyNum}`, {
            headers: {
                ...this.baseHeaders,
            },
        });

        return response.data;
    }

    async resendUnlockLoginMail({ userId }) {
        const response = await axios.get(
            `${this.baseUrl}/email/unlock/email/${userId}?target_url=${this.unlockLoginUrl}`,
            {
                headers: {
                    ...this.baseHeaders,
                },
            }
        );

        return response.data;
    }

    /*
     * 회원 정보 조회 API
     * 추가 필수 해더: "X-Login-Session"(로그인 하면 나오는 세션값, 로그인 시에 응답값으로 account.loginSessionID의 값을 의미함)
     */
    async getUserInfo({ loginSessionID }) {
        const response = await axios.get(`${this.baseUrl}/account/user/detail`, {
            headers: {
                ...this.baseHeaders,
                "X-Login-Session": loginSessionID,
            },
        });

        return response.data;
    }

    /*
     * 회원 가입 시 메일 인증 API
     * 필요 param: otp_key(메일에 쿼리스트링으로 넘어오는 authKeyNum의 값을 넣어주면 됨)
     */
    async verifyJoinEmail({ authKeyNum }) {
        const body = new URLSearchParams();
        body.append("otp_key", authKeyNum);

        const response = await axios.put(`${this.baseUrl}/account/auth/email`, body, {
            headers: {
                ...this.baseHeaders,
            },
        });

        return response.data;
    }

    /*
     * 약관 조회 API
     * 필요 쿼리스트링: term_data(약관 종류, 현재 두가지 사용중. "TOU_LAUNDRYINST", "PRV_LAUNDRYINST")
     */
    async getTerm({ termData }) {
        const response = await axios.get(`${this.baseUrl}/info/terms?term_data=${termData}`, {
            headers: {
                ...this.baseHeaders,
                "X-Device-Language": LMPAuthUtils.getTermLanguage({ countryCode: this.selectedCountry }),
                "X-Device-Language-Type": "IETF",
            },
        });

        return response.data;
    }

    /*
     * 비밀번호 변경 요청 API
     * 필요 쿼리스트링: user_id(이메일주소), target_url(메일에서 비밀번호 초기화 버튼 클릭시 리다이렉션 될 페이지,
     *                  아래에 있는 resetPassword 함수를 호출하여 비밀번호 변경 처리를 완료해야 함)
     */
    async sendResetPasswordMail({ userid }) {
        const targetUrl = `${this.frontServerBaseUrl}/auth/reset-password/confirm`;
        const response = await axios.get(
            `${this.baseUrl}/email/user/password?user_id=${userid}&target_url=${targetUrl}`,
            {
                headers: {
                    ...this.baseHeaders,
                },
            }
        );

        return response.data;
    }

    /*
     * 비밀번호 변경 요청 API
     * 필요 param: new_password, otp_key(메일에 쿼리스트링으로 넘어오는 authKeyNum의 값을 넣어주면 됨),
     * 비밀번호는 RSA 방식으로 암호화하여 전송
     */
    async resetPassword({ password, authKeyNum }) {
        const body = new URLSearchParams();
        body.append("new_password", EncryptUtil.encryptRSAPassword(password));
        body.append("otp_key", authKeyNum);

        const response = await axios.put(`${this.baseUrl}/account/user/password`, body, {
            headers: {
                ...this.baseHeaders,
            },
        });

        return response.data;
    }

    /*
     * 세션 만료 요청 API
     * loginSessionID만 넘겨주면 해당 세션 만료되어 기존에 사용하던 세션 만료 처리 됨.
     */
    async removeSession({ loginSessionID }) {
        const response = await axios.delete(`${this.baseUrl}/security/session?login_session=${loginSessionID}`, {
            headers: {
                ...this.baseHeaders,
            },
        });

        return response.data;
    }

    /*
     * 현재 로그인되어 있는 세션의 기간을 연장하는 API
     * 기본적으로 세션의 기한은 약 12000초, 즉 3시간 20분임.
     * 그 전에 세션을 연장해준다면 좋을듯?
     */
    async refreshSession({ loginSessionID }) {
        const response = await axios.put(
            `${this.baseUrl}/account/session`,
            {},
            {
                headers: {
                    ...this.baseHeaders,
                    "X-Login-Session": loginSessionID,
                },
            }
        );

        return response.data;
    }

    /*
     * 역할 승인 내역 및 승인 요청 목록 조회
     * apprStat은 N(미승인), Y(승인), C(반려), undefined(전체) 중 하나여야 합니다.
     */
    async getRoleApprovalList({ accessToken, page = 1, pageSize = 10, apprStat }) {
        const query = {
            reqPage: page,
            rowsPerPage: pageSize,
        };

        if (apprStat !== undefined) {
            if (apprStat !== "N" && apprStat !== "Y" && apprStat !== "C") {
                return "apprStat은 N(미승인), Y(승인), C(반려), undefined(전체) 중 하나여야 합니다.";
            }
            query.apprStat = apprStat;
        }

        if (this.fakeApi) {
            const curPage = Number(page) || 1;
            const size = Number(pageSize) || 10;
            let all = GetRoleApprovalListDummyData.role.roleApprList;
            if (apprStat !== undefined) {
                all = all.filter((item) => item.apprvFlag === apprStat);
            }
            const totCnt = all.length;
            const totPage = Math.max(1, Math.ceil(totCnt / size));
            const start = (curPage - 1) * size;
            return {
                ...GetRoleApprovalListDummyData,
                role: {
                    ...GetRoleApprovalListDummyData.role,
                    totPage: String(totPage),
                    reqPage: String(curPage),
                    rowsPerPage: String(size),
                    roleApprList: all.slice(start, start + size),
                },
            };
        }

        const response = await axios.get(`${this.baseUrl}/channel/role/apprlist`, {
            headers: {
                ...this.baseHeaders,
                "X-Login-Session": accessToken,
            },
            params: query,
        });

        return response.data;
    }

    /*
     * 역할 승인/반려 기능
     * apprFlag은 Y(승인) 또는 C(반려)여야 합니다.
     * apprFlag가 C일 경우 compCnt(반려 사유)는 undefined일 수 없습니다.
     */

    async processRole({ accessToken, usrNo, seqNo, apprFlag, compCnt }) {
        if (apprFlag !== "Y" && apprFlag !== "C") {
            return "apprFlag은 Y(승인) 또는 C(반려)여야 합니다.";
        }

        const data = {
            usrNo: usrNo,
            seqNo: seqNo,
            apprFlag: apprFlag,
            compCnt: compCnt,
        };

        const response = await axios.put(`${this.baseUrl}/channel/role/apprrtns`, data, {
            headers: {
                ...this.baseHeaders,
                "X-Login-Session": accessToken,
            },
        });

        return response.data;
    }

    /*
     *  유저 역할 변경 API
     *  roleId는 필수값 이며 존재해야 합니다.
     */
    async changeRole({ accessToken, roleId }) {
        const data = {
            roleId: roleId,
        };

        const response = await axios.post(`${this.baseUrl}/channel/role/user`, data, {
            headers: {
                ...this.baseHeaders,
                "X-Login-Session": accessToken,
            },
        });

        return response.data;
    }

    /*
     *  유저 역할 생성 API
     *  publicFlag는 기본 역할 여부를 설정하는 값(이유는 알 수 없지만 여러개의 역할이 "Y"어도 살관없는 것으로 보임.)
     *  tblhourOrd는 유저 역할 조회시 노출되는 순위로 보임. 숫자가 낮을수록 상위 노출됨. 다만 여러개의 Role이 같은 값을 가질 수 있는 것으로 보임.
     *  chanId는 생성할 채널의 ID 값
     *  approvers는 해당 역할을 승인할 수 있는 역할(예를 들어, ADM037001을 가진 유저가 지금 생성하는 역할을 승인/거절 할 수 있음)
     *  approversChanId는 해당 역할을 승인할 수 있는 역할의 채널 Id 값.
     *  만약 approvers가 다수라면 |를 구분자로 씀. (예를들어 ADM037001|TEST37001) 또한, approversChanId도 구분자를 추가해야함! 같은 채널의 Role이라도 ㅋㅋ (예시: CHN000037|CHN000037)
     */
    async createRole({
        accessToken,
        roleId,
        roleName,
        publicFlag = "N",
        basFlag = "N",
        tblhourOrd = 11,
        chanId = "CHN000037",
        approvers = "ADM037001",
        approversChanId = "CHN000037",
    }) {
        const data = {
            roleId: roleId,
            roleName: roleName,
            publicFlag: publicFlag,
            basFlag: basFlag,
            tblhourOrd: tblhourOrd,
            chanId: chanId,
            approvers: approvers,
            approversChanId: approversChanId,
        };

        try {
            const response = await axios.post(`${this.baseUrl}/channel/role`, data, {
                headers: {
                    ...this.baseHeaders,
                    "X-Login-Session": accessToken,
                },
            });
            return response.data;
        } catch (error) {
            console.error("createRole error:", error);
            return null;
        }
    }

    /*
     * 해당 유저 역할의 approvers로 들어가있는 유저가 usrNo으로 상세정보를 조회하는 API
     * 관리자가 usrNo를 조회한 뒤 상세 정보를 확인할 수 있는 방법.
     * 다만 개인정보(이메일 제외)에 대한 정보는 매우 부족하며, 이름, 전화번호, 주소 등은 Response Body에 없음.
     */
    async getUserInfoByAdmin({ accessToken, channelId = "CHN000037", usrNo }) {
        if (this.fakeApi) {
            return {
                resultCode: "0000",
                account: {
                    userID: `user${usrNo}@laundrycrew.com`,
                },
            };
        }

        const response = await axios.get(`${this.baseUrl}/channel/user/detail/${channelId}/${usrNo}`, {
            headers: {
                ...this.baseHeaders,
                "X-Login-Session": accessToken,
            },
        });

        return response.data;
    }
}

export default LMPAuthApi;
